import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Copy, Check, ExternalLink, MessageSquare, Calendar, Paperclip, Bell, Code2, BookOpen, KeyRound, AlertTriangle } from "lucide-react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { appConfig } from "@common-config/app.config";
import { Trans, useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

// Custom syntax highlighting styles - light mode style for both modes
const getCustomStyle = () => {
  return {
    'hljs': {
      display: 'block',
      overflowX: 'auto',
      padding: '0.5em',
      color: '#1e293b', // dark text
      background: '#f8f9fa', // light background
    },
    'hljs-comment': {
      color: '#6b7280',
      fontStyle: 'italic',
    },
    'hljs-quote': {
      color: '#6b7280',
      fontStyle: 'italic',
    },
    'hljs-keyword': {
      color: '#7c3aed', // purple for keywords like import
    },
    'hljs-selector-tag': {
      color: '#7c3aed',
    },
    'hljs-literal': {
      color: '#7c3aed',
    },
    'hljs-type': {
      color: '#7c3aed',
    },
    'hljs-string': {
      color: '#991b1b', // dark red/maroon for strings
    },
    'hljs-number': {
      color: '#991b1b',
    },
    'hljs-function': {
      color: '#2563eb', // softer blue for functions
    },
    'hljs-title': {
      color: '#2563eb',
    },
    'hljs-variable': {
      color: '#1e293b', // dark text for variables
    },
    'hljs-params': {
      color: '#2563eb', // softer blue for params
    },
    'hljs-built_in': {
      color: '#2563eb', // softer blue for built-ins
    },
    'hljs-attr': {
      color: '#1e293b', // dark text for attributes
    },
    'hljs-attribute': {
      color: '#1e293b',
    },
    'hljs-name': {
      color: '#1e293b',
    },
    'hljs-tag': {
      color: '#1e293b',
    },
    'hljs-section': {
      color: '#2563eb',
    },
    'hljs-meta': {
      color: '#2563eb',
    },
    'hljs-link': {
      color: '#2563eb',
    },
  } as any;
};

const ApiDocumentationView = () => {
  const { t } = useTranslation(namespace);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const CodeBlock = ({ code, language = "json", id }: { code: string; language?: string; id: string }) => {
    return (
      <div className="relative group">
        <Button
          variant="outline"
          size="sm"
          className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => copyToClipboard(code, id)}
        >
          {copiedCode === id ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              {t('apiDocs.copied', 'Copied!')}
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-2" />
              {t('apiDocs.copy', 'Copy')}
            </>
          )}
        </Button>
        <SyntaxHighlighter
          language={language}
          style={getCustomStyle()}
          customStyle={{
            margin: 0,
            padding: "1.5rem",
            backgroundColor: "#f8f9fa", // light background - same for both modes
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
            border: "1px solid #e5e7eb", // light border - same for both modes
            color: '#1e293b', // dark text - same for both modes
          }}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  };

  const endpoint = appConfig.API_ENDPOINT || "https://api.blueticks.co";
  // v1 recipient is a PATH segment: an international phone number in E.164
  // format (e.g. +15551234567) OR a WhatsApp chat id (e.g. 12345@c.us,
  // 1234567890@g.us, 12345@newsletter). This example uses a phone number.
  const exampleChatId = "+15551234567";
  const sendEndpoint = `${endpoint}/v1/messages/${exampleChatId}`;
  const scheduleEndpoint = `${endpoint}/v1/scheduled-messages/${exampleChatId}`;
  // Legacy endpoint kept for the deprecated section below.
  const legacyEndpoint = `${endpoint}/messages`;

  const simpleMessageExample = `curl --location '${sendEndpoint}' \\
--header 'Authorization: Bearer bt_live_your_api_key_here' \\
--header 'Content-Type: application/json' \\
--data '{
    "type": "text",
    "text": "This is a test message"
}'`;

  const scheduledMessageExample = `curl --location '${scheduleEndpoint}' \\
--header 'Authorization: Bearer bt_live_your_api_key_here' \\
--header 'Content-Type: application/json' \\
--data '{
    "type": "text",
    "text": "This is a scheduled test message",
    "sendAt": "2026-01-01T00:00:00.000Z"
}'`;

  const messageWithAttachmentExample = `curl --location '${sendEndpoint}' \\
--header 'Authorization: Bearer bt_live_your_api_key_here' \\
--header 'Content-Type: application/json' \\
--data '{
    "type": "media",
    "text": "Check out this file",
    "mediaUrl": "https://example.com/file.pdf"
}'`;

  const pollExample = `curl --location '${sendEndpoint}' \\
--header 'Authorization: Bearer bt_live_your_api_key_here' \\
--header 'Content-Type: application/json' \\
--data '{
    "type": "poll",
    "pollQuestion": "What is your favorite color?",
    "pollOptions": ["Red", "Green", "Blue"],
    "pollAllowMultiple": false
}'`;

  const javascriptExample = `fetch('${sendEndpoint}', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer bt_live_your_api_key_here',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    type: 'text',
    text: 'This is a test message'
  })
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`;

  const pythonExample = `import requests
import json

url = '${sendEndpoint}'
headers = {
    'Authorization': 'Bearer bt_live_your_api_key_here',
    'Content-Type': 'application/json',
}
data = {
    'type': 'text',
    'text': 'This is a test message'
}

response = requests.post(url, headers=headers, data=json.dumps(data))
print(response.json())`;

  const responseExample = `{
  "success": true,
  "data": {
    "id": "6a1b2c3d4e5f60718293a4b5",
    "to": "15551234567@c.us",
    "type": "text",
    "text": "This is a test message",
    "status": "pending",
    "createdAt": "2026-01-01T00:00:00.000Z"
  }
}`;

  const errorResponseExample = `{
  "success": false,
  "error": {
    "code": "authentication_required",
    "message": "Invalid API key",
    "requestId": "req_7f3c2a1b"
  }
}`;

  // ── Legacy (pre-v1) examples — body-key auth against the old /messages route.
  const legacySimpleExample = `curl --location '${legacyEndpoint}' \\
--header 'Content-Type: application/json' \\
--data '{
    "to": "+1800555555",
    "message": "This is a test message",
    "apiKey": "your-legacy-api-key-here"
}'`;

  // Shared inline elements for the <Trans> sentences below: <c> inline code,
  // <cc> the larger code pill, <b> emphasis.
  const CODE_PILL = "bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-sm text-slate-900 dark:text-slate-100";
  const rich = { c: <code className="text-xs" />, cc: <code className={CODE_PILL} />, b: <strong /> };

  const headerRows = [
    { name: "Authorization", value: "Bearer bt_live_..." },
    { name: "Content-Type", value: "application/json" },
  ];

  type BodyParam = { name: string; type: string; badge: { variant: "destructive" | "outline"; label: string }; descKey: string; desc: string };
  const required = { variant: "destructive" as const, label: t('apiDocs.params.required', 'Required') };
  const optional = { variant: "outline" as const, label: t('apiDocs.params.optional', 'Optional') };
  const requiredFor = (kind: string) => ({ variant: "destructive" as const, label: kind });
  const bodyParams: BodyParam[] = [
    { name: "type", type: "string", badge: required, descKey: 'apiDocs.params.type', desc: "One of <c>text</c>, <c>media</c>, or <c>poll</c>. Determines which fields below apply." },
    { name: "text", type: "string", badge: requiredFor("text"), descKey: 'apiDocs.params.text', desc: "Message body (required for <c>type: text</c>) or an optional caption for <c>type: media</c>. Up to 4096 chars." },
    { name: "mediaUrl", type: "string", badge: requiredFor("media"), descKey: 'apiDocs.params.mediaUrl', desc: "Public <c>https://</c> URL of the attachment (required for <c>type: media</c>)." },
    { name: "mediaKind", type: "string", badge: optional, descKey: 'apiDocs.params.mediaKind', desc: "image · video · audio · document · sticker · voice · gif. Auto-detected from the URL when omitted." },
    { name: "pollQuestion", type: "string", badge: requiredFor("poll"), descKey: 'apiDocs.params.pollQuestion', desc: "Poll question (required for <c>type: poll</c>)." },
    { name: "pollOptions", type: "string[]", badge: requiredFor("poll"), descKey: 'apiDocs.params.pollOptions', desc: "2–12 poll options (required for <c>type: poll</c>)." },
    { name: "sendAt", type: "string (ISO 8601)", badge: optional, descKey: 'apiDocs.params.sendAt', desc: "Schedule for a future time (UTC). Only on <c>/v1/scheduled-messages/{chatId}</c>. Must be 10s–365 days out." },
    { name: "replyTo", type: "string", badge: optional, descKey: 'apiDocs.params.replyTo', desc: "Wire <c>key</c> of a prior message to quote-reply." },
  ];

  const simpleExamples = [
    { label: "cURL", code: simpleMessageExample, language: "bash", id: "simple-curl" },
    { label: "JavaScript", code: javascriptExample, language: "javascript", id: "simple-js" },
    { label: "Python", code: pythonExample, language: "python", id: "simple-python" },
  ];

  const responseFields = [
    { name: "success", descKey: 'apiDocs.response.success', desc: "Always <c>true</c> on a successful response." },
    { name: "data.id", descKey: 'apiDocs.response.dataId', desc: "Handle for the message — the queue id for scheduled sends, or the WhatsApp message key for direct sends." },
    { name: "data.status", descKey: 'apiDocs.response.dataStatus', desc: "Lifecycle status: <c>pending</c>, <c>confirmed</c>, <c>received</c>, <c>read</c>, <c>played</c>, or <c>failed</c>." },
  ];

  const commonErrors = [
    { errKey: 'apiDocs.errors.e401', desc: "<b>401</b> — Missing or invalid API key (check the <c>Authorization: Bearer</c> header)" },
    { errKey: 'apiDocs.errors.e422', desc: "<b>422</b> — Validation failed (see <c>error.details</c> for the offending fields)" },
    { errKey: 'apiDocs.errors.e400', desc: "<b>400</b> — Invalid recipient chatId or malformed body" },
    { errKey: 'apiDocs.errors.e429', desc: "<b>429</b> — Rate limit exceeded — back off and retry" },
    { errKey: 'apiDocs.errors.notConnected', desc: "WhatsApp Web not connected (for non-Pro plan users)" },
  ];

  const MEDIA_KINDS = "image, video, audio, document, sticker, voice, gif";

  const overviewCards = [
    { icon: MessageSquare, iconClass: "text-blue-600 dark:text-blue-400", boxClass: "from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-blue-100 dark:border-blue-900", title: t('apiDocs.overview.directTitle', 'Direct Messaging'), bodyKey: 'apiDocs.overview.directBody', body: "Send messages using your personal WhatsApp account. WhatsApp Web must be actively open." },
    { icon: Calendar, iconClass: "text-purple-600 dark:text-purple-400", boxClass: "from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border-purple-100 dark:border-purple-900", title: t('apiDocs.overview.proTitle', 'Pro Plan'), bodyKey: 'apiDocs.overview.proBody', body: "Subscribers can send messages without keeping WhatsApp Web open." },
    { icon: Calendar, iconClass: "text-green-600 dark:text-green-400", boxClass: "from-green-50 to-blue-50 dark:from-green-950/30 dark:to-blue-950/30 border-green-100 dark:border-green-900", title: t('apiDocs.overview.schedulingTitle', 'Message Scheduling'), bodyKey: 'apiDocs.overview.schedulingBody', body: "Schedule messages for later by posting to <c>/v1/scheduled-messages/{chatId}</c> with a <c>sendAt</c> time in UTC." },
    { icon: Paperclip, iconClass: "text-orange-600 dark:text-orange-400", boxClass: "from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border-orange-100 dark:border-orange-900", title: t('apiDocs.overview.attachmentsTitle', 'Attachments'), bodyKey: 'apiDocs.overview.attachmentsBody', body: "Send an attachment with <c>type: \"media\"</c> and a public <c>mediaUrl</c> (https)." },
    { icon: Code2, iconClass: "text-indigo-600 dark:text-indigo-400", boxClass: "from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border-indigo-100 dark:border-indigo-900", title: t('apiDocs.overview.asyncTitle', 'Asynchronous Requests'), bodyKey: 'apiDocs.overview.asyncBody', body: "Message requests are processed asynchronously." },
    { icon: Bell, iconClass: "text-pink-600 dark:text-pink-400", boxClass: "from-pink-50 to-red-50 dark:from-pink-950/30 dark:to-red-950/30 border-pink-100 dark:border-pink-900", title: t('apiDocs.overview.deliveryTitle', 'Delivery Notifications'), bodyKey: 'apiDocs.overview.deliveryBody', body: "Track delivery with the message ack endpoints, or register a webhook to receive status updates." },
  ];

  const th = "border border-slate-300 dark:border-slate-700 px-4 py-2 text-left font-semibold text-slate-900 dark:text-slate-100";
  const td = "border border-slate-300 dark:border-slate-700 px-4 py-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 dark:from-slate-900 dark:via-slate-800/50 dark:to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
              <BookOpen className="h-8 w-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 relative z-10" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent leading-tight pb-1">
              {t('apiDocs.title', 'WhatsApp Messaging API Documentation')}
            </h1>
          </div>
          <p className="text-lg text-slate-700 dark:text-slate-300 font-medium">
            {t('apiDocs.subtitle', 'Send messages using your personal WhatsApp account')}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Badge variant="outline" className="gap-2">
              <ExternalLink className="h-3 w-3" />
              <a
                //href="https://blueticks.co/"
                href="https://chromewebstore.google.com/detail/blueticks-whatsapp-schedu/adgnjhngogijkkppficiiepmjebijinl"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                {t('apiDocs.downloadExtension', 'Download the extension')}
              </a>
            </Badge>
          </div>
        </div>

        {/* Authentication Card */}
        <Card className="border-2 border-blue-100 dark:border-blue-900 shadow-lg shadow-blue-100/50 dark:shadow-blue-900/20">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50/50 dark:from-blue-950/50 dark:to-purple-950/50 border-b border-blue-100 dark:border-blue-900">
            <CardTitle className="text-blue-900 dark:text-blue-100 flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              {t('apiDocs.auth.title', 'Authentication')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-slate-700 dark:text-slate-300">
              <Trans t={t} i18nKey="apiDocs.auth.body" defaults="Create an API key from your account's <b>API Keys</b> settings. Keys look like <cc>bt_live_...</cc>. Send it on every request in the <cc>Authorization</cc> header as a Bearer token:" components={rich} />
            </p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <code className="font-mono text-sm text-slate-800 dark:text-slate-200 break-all">
                {"Authorization: Bearer bt_live_your_api_key_here"}
              </code>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <Trans t={t} i18nKey="apiDocs.auth.secret" defaults="<b>Keep your key secret.</b> Treat it like a password — anyone with it can send messages from your WhatsApp account. Store it server-side, never in client-side code." components={rich} />
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Overview Card */}
        <Card className="border-2 border-blue-100 dark:border-blue-900 shadow-lg shadow-blue-100/50 dark:shadow-blue-900/20">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50/50 dark:from-blue-950/50 dark:to-purple-950/50 border-b border-blue-100 dark:border-blue-900">
            <CardTitle className="text-blue-900 dark:text-blue-100 flex items-center gap-2">
              <Code2 className="h-5 w-5" />
              {t('apiDocs.overview.title', 'Overview')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-slate-700 dark:text-slate-300 mb-4">
              {t('apiDocs.overview.body', 'This API enables users to send messages through WhatsApp from their personal account. With features like message scheduling and attachment support, it provides flexibility for automated messaging campaigns. The Pro plan offers enhanced capabilities, including sending messages without the need to keep WhatsApp Web open.')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {overviewCards.map(({ icon: Icon, iconClass, boxClass, title, bodyKey, body }) => (
                <div key={bodyKey} className={`flex items-start gap-3 p-4 bg-gradient-to-r ${boxClass} rounded-lg border`}>
                  <Icon className={`h-5 w-5 ${iconClass} mt-0.5 shrink-0`} />
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">{title}</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <Trans t={t} i18nKey={bodyKey} defaults={body} components={rich} />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Endpoint Card */}
        <Card className="border-2 border-purple-100 dark:border-purple-900 shadow-lg shadow-purple-100/50 dark:shadow-purple-900/20">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50/50 dark:from-purple-950/50 dark:to-pink-950/50 border-b border-purple-100 dark:border-purple-900">
            <div className="flex items-center gap-2">
              <CardTitle className="text-purple-900 dark:text-purple-100">{t('apiDocs.send.title', 'Send a Message')}</CardTitle>
              <Badge className="bg-green-600 hover:bg-green-600 text-white">{t('apiDocs.send.current', 'Current')}</Badge>
            </div>
            <CardDescription className="text-purple-700 dark:text-purple-300 font-mono text-base">
              {`POST ${endpoint}/v1/messages/{chatId}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              <p className="text-slate-700 dark:text-slate-300">
                <Trans t={t} i18nKey="apiDocs.send.body" defaults="Send a message immediately. The recipient is the <cc>{chatId}</cc> path segment. To <b>schedule</b> a message instead, post to <cc>POST {{endpoint}}/v1/scheduled-messages/{chatId}</cc> with a <cc>sendAt</cc> field." values={{ endpoint }} components={rich} />
              </p>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">{t('apiDocs.send.headers', 'Headers')}</h3>
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 space-y-3">
                  {headerRows.map(({ name, value }) => (
                    <div key={name} className="flex items-center justify-between">
                      <span className="font-mono text-sm text-slate-700 dark:text-slate-300">{name}</span>
                      <Badge variant="secondary">{value}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">{t('apiDocs.send.pathParameter', 'Path Parameter')}</h3>
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    <Trans t={t} i18nKey="apiDocs.send.chatIdParam" defaults="<id>chatId</id> — the recipient. Either an international phone number in E.164 format (e.g. <c>+15551234567</c>) for a 1:1 chat, or a WhatsApp chat id: <c>{id}@g.us</c> for a group, <c>{id}@newsletter</c> for a channel, or <c>{phone}@c.us</c> for an individual." components={{ ...rich, id: <code className="font-mono text-slate-900 dark:text-slate-100" /> }} />
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">{t('apiDocs.params.title', 'Request Body Parameters')}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-slate-800">
                        <th className={th}>{t('apiDocs.params.parameter', 'Parameter')}</th>
                        <th className={th}>{t('apiDocs.params.typeHeader', 'Type')}</th>
                        <th className={th}>{t('apiDocs.params.requiredHeader', 'Required')}</th>
                        <th className={th}>{t('apiDocs.params.description', 'Description')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bodyParams.map((param, i) => (
                        <tr key={param.name} className={i % 2 === 1 ? "bg-slate-50 dark:bg-slate-800/50" : undefined}>
                          <td className={`${td} font-mono text-sm text-slate-900 dark:text-slate-100`}>{param.name}</td>
                          <td className={`${td} text-sm text-slate-700 dark:text-slate-300`}>{param.type}</td>
                          <td className={td}>
                            <Badge variant={param.badge.variant}>{param.badge.label}</Badge>
                          </td>
                          <td className={`${td} text-sm text-slate-700 dark:text-slate-300`}>
                            <Trans t={t} i18nKey={param.descKey} defaults={param.desc} components={rich} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Examples Section */}
        <Card className="border-2 border-green-100 dark:border-green-900 shadow-lg shadow-green-100/50 dark:shadow-green-900/20">
          <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50/50 dark:from-green-950/50 dark:to-blue-950/50 border-b border-green-100 dark:border-green-900">
            <CardTitle className="text-green-900 dark:text-green-100">{t('apiDocs.examples.title', 'Code Examples')}</CardTitle>
            <CardDescription className="text-green-700 dark:text-green-300">
              {t('apiDocs.examples.subtitle', 'Examples for different use cases and programming languages')}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <Tabs defaultValue="simple" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="simple">{t('apiDocs.examples.simple', 'Simple Message')}</TabsTrigger>
                <TabsTrigger value="scheduled">{t('apiDocs.examples.scheduled', 'Scheduled')}</TabsTrigger>
                <TabsTrigger value="attachment">{t('apiDocs.examples.attachment', 'With Attachment')}</TabsTrigger>
                <TabsTrigger value="poll">{t('apiDocs.examples.poll', 'Poll')}</TabsTrigger>
              </TabsList>

              <TabsContent value="simple" className="space-y-4">
                {simpleExamples.map(({ label, code, language, id }) => (
                  <div key={id}>
                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{label}</h4>
                    <CodeBlock code={code} language={language} id={id} />
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="scheduled" className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{"cURL"}</h4>
                  <CodeBlock code={scheduledMessageExample} language="bash" id="scheduled-curl" />
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <Trans t={t} i18nKey="apiDocs.examples.scheduledNote" defaults="<b>Note:</b> Scheduling uses the <c>/v1/scheduled-messages/{chatId}</c> endpoint. The <c>sendAt</c> must be in UTC (ISO 8601), between 10 seconds and 365 days in the future." components={rich} />
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="attachment" className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{"cURL"}</h4>
                  <CodeBlock code={messageWithAttachmentExample} language="bash" id="attachment-curl" />
                </div>
                <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <p className="text-sm text-orange-800 dark:text-orange-200 mb-2">
                    <Trans t={t} i18nKey="apiDocs.examples.attachmentNote" defaults="<b>Important:</b> Set <c>type: &quot;media&quot;</c> and provide a publicly accessible <c>https://</c> <c>mediaUrl</c>. The kind is auto-detected; override it with <c>mediaKind</c> if needed. Common kinds:" components={rich} />
                  </p>
                  <ul className="list-disc list-inside text-sm text-orange-700 dark:text-orange-300 space-y-1">
                    <li>{MEDIA_KINDS}</li>
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="poll" className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{"cURL"}</h4>
                  <CodeBlock code={pollExample} language="bash" id="poll-curl" />
                </div>
                <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    <Trans t={t} i18nKey="apiDocs.examples.pollNote" defaults="<b>Poll:</b> Set <c>type: &quot;poll&quot;</c> with a <c>pollQuestion</c> and 2–12 <c>pollOptions</c>. Set <c>pollAllowMultiple: true</c> to let recipients select more than one." components={rich} />
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Response Section */}
        <Card className="border-2 border-indigo-100 dark:border-indigo-900 shadow-lg shadow-indigo-100/50 dark:shadow-indigo-900/20">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50/50 dark:from-indigo-950/50 dark:to-purple-950/50 border-b border-indigo-100 dark:border-indigo-900">
            <CardTitle className="text-indigo-900 dark:text-indigo-100">{t('apiDocs.response.title', 'Response Format')}</CardTitle>
            <CardDescription className="text-indigo-700 dark:text-indigo-300">
              {t('apiDocs.response.subtitle', 'Success and error response examples')}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <Tabs defaultValue="success" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="success">{t('apiDocs.response.successTab', 'Success Response')}</TabsTrigger>
                <TabsTrigger value="error">{t('apiDocs.response.errorTab', 'Error Response')}</TabsTrigger>
              </TabsList>

              <TabsContent value="success">
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('apiDocs.response.statusCode', 'Status Code: {{code}}', { code: '200 OK' })}</h4>
                  <CodeBlock code={responseExample} language="json" id="success-response" />
                  <div className="mt-4 space-y-2">
                    {responseFields.map(({ name, descKey, desc }) => (
                      <div key={name} className="flex items-start gap-2">
                        <Badge variant="outline" className="mt-1">{name}</Badge>
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          <Trans t={t} i18nKey={descKey} defaults={desc} components={rich} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="error">
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('apiDocs.response.statusCode', 'Status Code: {{code}}', { code: '4xx / 5xx' })}</h4>
                  <CodeBlock code={errorResponseExample} language="json" id="error-response" />
                  <div className="mt-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      <strong>{t('apiDocs.errors.title', 'Common Errors:')}</strong>
                    </p>
                    <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 mt-2 space-y-1">
                      {commonErrors.map(({ errKey, desc }) => (
                        <li key={errKey}><Trans t={t} i18nKey={errKey} defaults={desc} components={rich} /></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Usage Guide */}
        <Card className="border-2 border-amber-100 dark:border-amber-900 shadow-lg shadow-amber-100/50 dark:shadow-amber-900/20">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50/50 dark:from-amber-950/50 dark:to-orange-950/50 border-b border-amber-100 dark:border-amber-900">
            <CardTitle className="text-amber-900 dark:text-amber-100">{t('apiDocs.guide.title', 'Usage Guide')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                {t('apiDocs.guide.sendingTitle', 'Sending a Message')}
              </h3>
              <div className="space-y-2 text-slate-700 dark:text-slate-300">
                <p><Trans t={t} i18nKey="apiDocs.guide.sendingStandard" defaults="<b>Standard Message:</b> Ensure WhatsApp Web is open. POST to <c>/v1/messages/{chatId}</c> with your Bearer key and a <c>type: &quot;text&quot;</c> body." components={rich} /></p>
                <p><Trans t={t} i18nKey="apiDocs.guide.sendingPro" defaults="<b>With Pro Plan:</b> If you've purchased the Pro plan, you can send messages without keeping WhatsApp Web open." components={rich} /></p>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
                {t('apiDocs.guide.schedulingTitle', 'Scheduling a Message')}
              </h3>
              <div className="space-y-2 text-slate-700 dark:text-slate-300">
                <p><Trans t={t} i18nKey="apiDocs.guide.schedulingBody" defaults="POST to <cc>/v1/scheduled-messages/{chatId}</cc> and include a <cc>sendAt</cc> field in UTC." components={rich} /></p>
                <p className="text-sm text-slate-600 dark:text-slate-400 italic">
                  {t('apiDocs.guide.schedulingExample', 'Example: "{{iso}}" represents January 1st, 2026 at midnight UTC.', { iso: '2026-01-01T00:00:00.000Z' })}
                </p>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                <Paperclip className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                {t('apiDocs.guide.attachmentTitle', 'Adding an Attachment')}
              </h3>
              <div className="space-y-2 text-slate-700 dark:text-slate-300">
                <p><Trans t={t} i18nKey="apiDocs.guide.attachmentIntro" defaults="Set <cc>type: &quot;media&quot;</cc> and provide:" components={rich} /></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><Trans t={t} i18nKey="apiDocs.guide.attachmentMediaUrl" defaults="<b>mediaUrl:</b> A public <c>https://</c> URL of the file" components={rich} /></li>
                  <li><Trans t={t} i18nKey="apiDocs.guide.attachmentText" defaults="<b>text</b> (optional): a caption shown with the media" components={rich} /></li>
                  <li><Trans t={t} i18nKey="apiDocs.guide.attachmentMediaKind" defaults="<b>mediaKind</b> (optional): image, video, audio, document, sticker, voice, or gif — auto-detected when omitted" components={rich} /></li>
                </ul>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                <Bell className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                {t('apiDocs.guide.deliveryTitle', 'Delivery Status')}
              </h3>
              <div className="space-y-2 text-slate-700 dark:text-slate-300">
                <p><Trans t={t} i18nKey="apiDocs.guide.deliveryBody" defaults="Track delivery by reading the message <cc>status</cc> (poll the message ack endpoints), or register a webhook to receive delivery-status updates as they happen." components={rich} /></p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legacy Section */}
        <Card className="border-2 border-slate-200 dark:border-slate-700 shadow-lg opacity-95">
          <CardHeader className="bg-slate-100/70 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <CardTitle className="text-slate-800 dark:text-slate-200">{t('apiDocs.legacy.title', 'Legacy Method (deprecated)')}</CardTitle>
              <Badge variant="secondary">{t('apiDocs.legacy.badge', 'Legacy')}</Badge>
            </div>
            <CardDescription className="text-slate-600 dark:text-slate-400 font-mono text-sm">
              {`POST ${legacyEndpoint}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <Trans t={t} i18nKey="apiDocs.legacy.body" defaults="This is the older method: the recipient and the key travel in the JSON body (<c>to</c>, <c>message</c>, <c>apiKey</c>). It only accepts keys created <b>before</b> the current API. New <c>bt_live_...</c> keys will <b>not</b> work here — use the <b>Send a Message</b> method above with the <c>Authorization: Bearer</c> header. Kept working for existing integrations; new integrations should use the current method." components={rich} />
                </span>
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('apiDocs.legacy.curl', 'cURL (legacy)')}</h4>
              <CodeBlock code={legacySimpleExample} language="bash" id="legacy-curl" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApiDocumentationView;
